import { Menu as AntMenu, MenuProps } from 'antd';
import styled, { createGlobalStyle, css } from 'styled-components';
import { Delete } from './delete';
import { Edit } from './edit';
import { DropdownHeader } from './header';
import { Link } from './link';
import { HeadingCompact00, Short01 } from '../../styles';

const getBaseMenuStyle = (prefix: string) => css`
  min-width: 240px;
  max-height: 70vh;
  overflow: auto;
  padding: var(--spacing-02);
  border-radius: var(--border-radius-small);
  border: none;

  ${prefix}-menu-item-group-list {
    margin: 0;
  }
  ${prefix}-menu-item-group-title {
    padding: 0 var(--spacing-03);
    ${HeadingCompact00};
    height: var(--height-header);
    display: flex;
    align-items: center;
  }
  ${prefix}-menu-item,
  ${prefix}-menu-submenu-title {
    display: flex;
    align-items: center;
    padding: var(--spacing-02) var(--spacing-05);
    min-height: var(--height-row);
    height: auto;
    margin: 0;
    ${Short01};
    border-radius: var(--border-radius-default);
    &:hover:not(${prefix}-menu-item-selected) {
      background-color: var(--cell-hover);
      color: inherit;
    }
    ${prefix}-menu-submenu-expand-icon {
      color: var(--icon-secondary);
    }
  }
  ${prefix}-menu-item-selected {
    background-color: var(--cell-selected-background);
  }
  ${prefix}-menu-submenu${prefix}-menu-submenu-active {
    ${prefix}-menu-submenu-title {
      color: initial;
    }
    ${prefix}-menu-submenu-expand-icon {
      color: var(--icon-secondary);
    }
    ${prefix}-menu-item-icon {
      color: initial;
    }
  }
  ${prefix}-menu-submenu-selected {
    ${prefix}-menu-submenu-title {
      color: initial;
    }
  }
  ${prefix}-menu-item-selected::after {
    opacity: 0;
  }
  ${prefix}-menu-title-content {
    margin: 0;
  }
  ${prefix}-menu-item&-menu-item-danger&-menu-item-disabled {
    color: var(--gray-40);
    &:hover {
      color: var(--gray-40);
      background-color: var(--white-100);
    }
  }
  ${prefix}-menu-item&-menu-item-danger {
    &:hover {
      background-color: var(--red-60);
    }
  }
  ${prefix}-menu-item .ant-menu-item-icon,
  ${prefix}-menu-item ${prefix}-menu-item-icon,
  ${prefix}-menu-submenu > ${prefix}-menu-submenu-title > svg {
    margin-right: var(--spacing-03);
  }
  ${prefix}-menu-item-divider {
    margin: var(--spacing-02) 0;
  }
  ${prefix}-menu-item-danger:hover {
    background-color: var(--red-10) !important;
    color: var(--error-color) !important;
  }
`;
const MenuStyle = css`
  ${getBaseMenuStyle('.ant')};
  ${getBaseMenuStyle('.ant-dropdown')};
`;
const GlobalSubmenuStyle = createGlobalStyle`
  .ant-dropdown-menu-submenu-popup {
    .ant-dropdown-menu {
      ${MenuStyle};
      min-width: unset;
    }
  }
`;
const StyledMenu = styled(AntMenu)`
  ${MenuStyle};

  .ant-menu-item {
    margin-bottom: 0 !important;
  }
  .ant-menu-item-selected {
    background-color: var(--blue-20) !important;
  }
`;

interface CustomMenuComponent extends React.FC<MenuProps> {
  Header: typeof DropdownHeader;
  ItemDelete: typeof Delete;
  ItemEdit: typeof Edit;
  Link: typeof Link;
  SubMenu: typeof AntMenu.SubMenu;
  ItemGroup: typeof AntMenu.ItemGroup;
  Item: typeof AntMenu.Item;
  Divider: typeof AntMenu.Divider;
}

const MenuComponent: React.FC<MenuProps> = (props) => {
  return (
    <>
      <GlobalSubmenuStyle />
      <StyledMenu {...props} />
    </>
  );
};

const Menu = MenuComponent as CustomMenuComponent;

Menu.Header = DropdownHeader;
Menu.ItemDelete = Delete;
Menu.ItemEdit = Edit;
Menu.Link = Link;
Menu.SubMenu = AntMenu.SubMenu;
Menu.ItemGroup = AntMenu.ItemGroup;
Menu.Item = AntMenu.Item;
Menu.Divider = AntMenu.Divider;

const SidebarMenu = styled(Menu)`
  padding: 0;
  margin: 0;
  height: auto;
  max-height: unset;
  border-radius: 0;
  .ant-menu-sub.ant-menu-inline {
    background: transparent;
  }
  .ant-menu-submenu-title,
  .ant-menu-item {
    padding: 0 var(--spacing-03);
  }
  .ant-menu-submenu {
    .ant-menu-submenu-title,
    .ant-menu-item {
      padding-left: var(--spacing-07) !important;
    }
    .ant-menu-submenu-expand-icon {
      right: unset;
      left: var(--spacing-03);
    }
  }
  .ant-menu-item {
    padding-left: var(--spacing-03) !important;
  }
`;

export { Menu, SidebarMenu };
